import React, { useState, useEffect, useRef, useCallback } from 'react';
import { watchlistSelfService } from '../services/watchlistSelfService';
import SelectDropdown from './SelectDropdown';
import { useWatchlistSelfStore } from '../stores/useWatchlistSelfStore';
import { useToastStore } from '../stores/useToastStore';
import { useAppStore } from '../stores/useAppStore';

interface AvailableCoin {
  id: string;
  symbol: string;
  name: string;
  image: string;
  currentPrice: number;
  marketCap: number;
  fullyDilutedValuation: number;
  totalVolume: number;
  totalSupply: number;
  maxSupply: number;
  circulatingSupply: number;
  priceChange24h: number;
}

interface AddCoinDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const AddCoinDialog: React.FC<AddCoinDialogProps> = ({ isOpen, onClose }) => {
  const [availableCoins, setAvailableCoins] = useState<AvailableCoin[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState(''); // 搜索输入框的值
  const [searchTerm, setSearchTerm] = useState(''); // 实际用于搜索的关键词
  const [sortBy, setSortBy] = useState<string>('default'); // 排序方式
  const [viewportHeight, setViewportHeight] = useState<number>(window.innerHeight);
  const pageSize = 10;
  const { items: watchlistItems, addingSymbols } = useWatchlistSelfStore();
  const showToast = useToastStore((state) => state.show);
  const hasLoadedRef = useRef(false);
  const loadAvailableCoinsRef = useRef<((page: number, search?: string, sort?: string) => Promise<void>) | null>(null);

  const loadAvailableCoins = useCallback(
    async (page: number = 1, search?: string, sort?: string) => {
      // 防止重复请求 - 直接从 store 获取最新状态
      const currentState = useAppStore.getState();
      if (currentState.isFetchingAvailableCoins) {
        console.log('⏳ 可添加币列表正在获取中，跳过重复调用');
        return;
      }

      useAppStore.setState({ isFetchingAvailableCoins: true });
      setLoading(true);
      setError(null);
      try {
        const result = await watchlistSelfService.getAvailableCoins(page, pageSize, search, sort || sortBy);
        setAvailableCoins(result.data);
        setTotalPages(result.pagination.totalPages);
        setTotal(result.pagination.total);
      } catch (err: any) {
        setError(err.message || '加载可添加币列表失败');
        console.error('加载可添加币列表失败:', err);
      } finally {
        setLoading(false);
        useAppStore.setState({ isFetchingAvailableCoins: false });
      }
    },
    [pageSize, sortBy],
  );

  // 将函数引用存储到 ref 中，避免 useEffect 依赖项变化
  loadAvailableCoinsRef.current = loadAvailableCoins;

  // 监听视口高度变化（处理移动端键盘弹起）
  useEffect(() => {
    if (!isOpen) return;

    const updateHeight = () => {
      const newViewportHeight = window.visualViewport?.height || window.innerHeight;
      setViewportHeight(newViewportHeight);
    };

    // 初始计算
    updateHeight();

    // 使用 visualViewport API（更好的移动端支持）
    if (window.visualViewport) {
      const handleViewportChange = () => {
        updateHeight();
      };
      window.visualViewport.addEventListener('resize', handleViewportChange);
      window.visualViewport.addEventListener('scroll', handleViewportChange);

      return () => {
        window.visualViewport?.removeEventListener('resize', handleViewportChange);
        window.visualViewport?.removeEventListener('scroll', handleViewportChange);
      };
    } else {
      // 降级方案：使用 window resize
      window.addEventListener('resize', updateHeight);
      window.addEventListener('orientationchange', updateHeight);

      return () => {
        window.removeEventListener('resize', updateHeight);
        window.removeEventListener('orientationchange', updateHeight);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      setCurrentPage(1);
      setSearchInput('');
      setSearchTerm('');
      // 直接加载可添加币列表，不刷新自选列表
      // 使用 ref 中的函数引用，避免依赖项问题
      if (loadAvailableCoinsRef.current) {
        loadAvailableCoinsRef.current(1);
      }
    } else if (!isOpen) {
      hasLoadedRef.current = false;
      setSearchInput('');
      setSearchTerm('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      loadAvailableCoins(newPage, searchTerm || undefined);
    }
  };

  // 执行搜索
  const handleSearch = () => {
    const trimmedSearch = searchInput.trim();
    setSearchTerm(trimmedSearch);
    setCurrentPage(1);
    if (loadAvailableCoinsRef.current) {
      loadAvailableCoinsRef.current(1, trimmedSearch || undefined);
    }
  };

  const handleSearchInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleAddCoin = async (coin: AvailableCoin) => {
    try {
      // 添加到自选，使用 SWAP 类型（因为我的自选默认是 SWAP）
      const symbol = `${coin.symbol}-USDT-SWAP`;
      // 传递 coinId 用于生成 coin_base 数据
      // 使用 store 的 addToWatchlistSelf 方法，它会手动维护列表，不调用接口
      const { addToWatchlistSelf } = useWatchlistSelfStore.getState();
      await addToWatchlistSelf(symbol, 'SWAP', coin.id);
      // 不关闭对话框，不重新拉取可添加币列表
    } catch (err: any) {
      showToast(err.message || '添加失败', 'error');
      console.error('添加币种失败:', err);
    }
  };

  const formatNumber = (value: number | null | undefined): string => {
    if (!value) return '-';
    if (value >= 1e12) return (value / 1e12).toFixed(2) + 'T';
    if (value >= 1e9) return (value / 1e9).toFixed(2) + 'B';
    if (value >= 1e6) return (value / 1e6).toFixed(2) + 'M';
    if (value >= 1e3) return (value / 1e3).toFixed(2) + 'K';
    return value.toFixed(2);
  };

  // 检查币种是否已在自选列表中
  const isCoinInWatchlist = (coinSymbol: string): boolean => {
    const symbolToCheck = `${coinSymbol}-USDT-SWAP`;
    return watchlistItems.some((item) => item.symbol === symbolToCheck);
  };

  // 检查币种是否正在添加中
  const isCoinAdding = (coinSymbol: string): boolean => {
    const symbolToCheck = `${coinSymbol}-USDT-SWAP`;
    // key 的格式是 `${symbol}-${instType}`，所以是 `${symbolToCheck}-SWAP`
    return addingSymbols.has(`${symbolToCheck}-SWAP`);
  };

  if (!isOpen) return null;

  // 计算对话框高度：视口高度的 95%，但不超过实际可用高度
  const dialogHeight = Math.min(viewportHeight * 0.95, viewportHeight - 20);

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-start justify-center bg-black/50 backdrop-blur-sm overflow-y-auto"
      style={{
        paddingTop: '2vh',
        paddingBottom: '2vh',
      }}
      onClick={(e) => {
        // 点击遮罩层关闭对话框
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="relative bg-gradient-to-br from-purple-900/95 via-blue-900/95 to-indigo-900/95 border border-white/20 rounded-xl w-full max-w-2xl mx-4 flex flex-col shadow-2xl"
        style={{
          height: `${dialogHeight}px`,
          maxHeight: `${viewportHeight - 20}px`,
          // 确保对话框在键盘弹起时也能正确显示
          position: 'relative',
          zIndex: 1,
          // 确保对话框可以滚动
          overflow: 'hidden',
        }}
        onClick={(e) => {
          // 阻止点击事件冒泡，防止点击对话框内容时关闭
          e.stopPropagation();
        }}
      >
        {/* 背景光晕效果 - 确保在最底层，不影响交互 */}
        <div
          className="absolute top-0 left-0 w-full h-full bg-gradient-to-tr from-cyan-500/10 via-transparent to-purple-500/10 rounded-xl pointer-events-none"
          style={{ zIndex: 0 }}
        />
        <div
          className="absolute top-1/4 left-1/4 w-64 h-64 bg-cyan-400/10 rounded-full blur-3xl pointer-events-none"
          style={{ zIndex: 0 }}
        />
        <div
          className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-purple-400/10 rounded-full blur-3xl pointer-events-none"
          style={{ zIndex: 0 }}
        />

        {/* 内容 - 确保在背景之上 */}
        <div className="relative flex flex-col h-full" style={{ zIndex: 10 }}>
          {/* 头部 */}
          <div className="flex items-center justify-between p-4 border-b border-white/10 flex-shrink-0">
            <h2 className="text-xl font-semibold text-white">添加币种</h2>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* 搜索框 */}
          <div className="p-4 border-b border-white/10 flex-shrink-0 relative z-30">
            <div className="relative">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleSearchInputKeyDown}
                placeholder="搜索币种符号"
                className="w-full px-4 py-2 pl-10 pr-24 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all"
                inputMode="search"
                enterKeyHint="search"
                onFocus={(e) => {
                  // 当输入框获得焦点时，确保输入框可见
                  setTimeout(() => {
                    // 使用 scrollIntoView 确保输入框在视口中可见
                    // 使用 block: 'center' 将输入框滚动到视口中心
                    e.target.scrollIntoView({
                      behavior: 'smooth',
                      block: 'center',
                      inline: 'nearest',
                    });

                    // 额外处理：如果输入框仍然被遮挡，再次尝试滚动
                    setTimeout(() => {
                      const inputRect = e.target.getBoundingClientRect();
                      const viewportHeight = window.visualViewport?.height || window.innerHeight;

                      // 如果输入框在视口下方（被键盘遮挡），向上滚动
                      if (inputRect.bottom > viewportHeight - 50) {
                        const scrollAmount = inputRect.bottom - viewportHeight + 100;
                        window.scrollBy({
                          top: scrollAmount,
                          behavior: 'smooth',
                        });
                      }
                    }, 200);
                  }, 100);
                }}
                style={{
                  position: 'relative',
                  zIndex: 30,
                }}
              />
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1 z-40">
                {searchInput && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSearchInput('');
                      setSearchTerm('');
                      setCurrentPage(1);
                      if (loadAvailableCoinsRef.current) {
                        loadAvailableCoinsRef.current(1);
                      }
                    }}
                    className="w-6 h-6 flex items-center justify-center text-white/40 hover:text-white/60 transition-colors cursor-pointer"
                    style={{
                      position: 'relative',
                      zIndex: 50,
                      minWidth: '24px',
                      minHeight: '24px',
                    }}
                    aria-label="清除搜索"
                  >
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* 排序下拉框 */}
          <div className="px-4 py-2 border-b border-white/10 flex-shrink-0 relative z-30">
            <SelectDropdown
              label="排序方式"
              value={sortBy}
              onChange={(value) => {
                setSortBy(value as string);
                setCurrentPage(1);
                if (loadAvailableCoinsRef.current) {
                  loadAvailableCoinsRef.current(1, searchTerm, value as string);
                }
              }}
              options={[
                { value: 'default', label: '默认排序（字母顺序）' },
                { value: 'volume', label: '按成交量排序' },
                { value: 'market_cap', label: '按市值排序' },
              ]}
              placeholder="请选择排序方式"
            />
          </div>

          {/* 内容区域 */}
          <div
            className="flex-1 overflow-y-auto p-4"
            style={{
              minHeight: 0,
              WebkitOverflowScrolling: 'touch', // iOS 平滑滚动
            }}
          >
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto"></div>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <div className="text-red-400 mb-4">{error}</div>
                <button
                  onClick={() => loadAvailableCoins(currentPage)}
                  className="px-4 py-2 bg-blue-500/20 text-blue-300 border border-blue-500/50 rounded-lg hover:bg-blue-500/30 transition-colors"
                >
                  重试
                </button>
              </div>
            ) : availableCoins.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-white/60">暂无可添加的币种</div>
              </div>
            ) : (
              <div className="space-y-3">
                {availableCoins.map((coin) => (
                  <div
                    key={coin.id}
                    className="bg-white/5 border border-white/20 rounded-xl p-4 hover:bg-white/10 transition-colors"
                  >
                    {/* 第一行：币种图标+名称+添加按钮 */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center flex-1 min-w-0">
                        {coin.image && (
                          <img
                            src={coin.image}
                            alt={coin.symbol}
                            className="w-10 h-10 rounded-full mr-3 flex-shrink-0"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-white text-base truncate">{coin.symbol}</h3>
                          <p className="text-sm text-white/60 truncate">{coin.name}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleAddCoin(coin)}
                        disabled={isCoinInWatchlist(coin.symbol) || isCoinAdding(coin.symbol)}
                        className={`px-4 py-2 rounded-lg text-sm flex-shrink-0 transition-colors flex items-center gap-2 ${
                          isCoinInWatchlist(coin.symbol) || isCoinAdding(coin.symbol)
                            ? 'bg-gray-500/20 text-gray-400 border border-gray-500/50 cursor-not-allowed'
                            : 'bg-green-500/20 text-green-300 border border-green-500/50 hover:bg-green-500/30'
                        }`}
                      >
                        {isCoinAdding(coin.symbol) ? (
                          <>
                            <svg
                              className="animate-spin h-4 w-4"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            <span>添加中</span>
                          </>
                        ) : (
                          '添加'
                        )}
                      </button>
                    </div>

                    {/* 第二行：币种数据 */}
                    <div className="space-y-2 text-sm">
                      {coin.fullyDilutedValuation && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400">完全稀释估值:</span>
                          <span className="text-white font-mono">${formatNumber(coin.fullyDilutedValuation)}</span>
                        </div>
                      )}
                      {coin.totalVolume && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400">总交易量:</span>
                          <span className="text-white font-mono">${formatNumber(coin.totalVolume)}</span>
                        </div>
                      )}
                      {coin.totalSupply && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400">总供应量:</span>
                          <span className="text-white font-mono">{formatNumber(coin.totalSupply)}</span>
                        </div>
                      )}
                      {coin.circulatingSupply && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400">流通供应量:</span>
                          <span className="text-white font-mono">{formatNumber(coin.circulatingSupply)}</span>
                        </div>
                      )}
                      {/* 流通率 */}
                      {(() => {
                        const circulating = coin.circulatingSupply || null;
                        const maxSupply = coin.maxSupply || null;
                        const totalSupply = coin.totalSupply || null;

                        let circulationRate: number | null = null;
                        if (circulating && circulating > 0) {
                          if (maxSupply && maxSupply > 0) {
                            circulationRate = (circulating / maxSupply) * 100;
                          } else if (totalSupply && totalSupply > 0) {
                            circulationRate = (circulating / totalSupply) * 100;
                          }
                        }

                        return circulationRate !== null ? (
                          <div className="flex justify-between items-center">
                            <span className="text-gray-400">流通率:</span>
                            <span className="text-white font-mono">{circulationRate.toFixed(2)}%</span>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 分页控件 */}
          {!loading && !error && availableCoins.length > 0 && totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t border-white/10">
              <div className="text-sm text-white/60 whitespace-nowrap">
                共 {total} 个币种，第 {currentPage} / {totalPages} 页
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="w-10 h-10 flex items-center justify-center bg-white/10 text-white border border-white/20 rounded-lg hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="上一页"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="w-10 h-10 flex items-center justify-center bg-white/10 text-white border border-white/20 rounded-lg hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="下一页"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddCoinDialog;
